// Dashboard sayfası - Ana sayfa ve istatistikler
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  FiUsers, 
  FiFolder, 
  FiCalendar, 
  FiTrendingUp,
  FiClock,
  FiActivity,
  FiMapPin,
  FiTag,
  FiHome,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiUser,
  FiType,
  FiEye,
  FiEyeOff,
  FiTarget,
  FiDollarSign,
  FiAward,
  FiStar,
  FiCheckCircle,
  FiXCircle,
  FiPause,
  FiPlay,
  FiFileText,
  FiThumbsUp,
  FiThumbsDown,
  FiMaximize
} from 'react-icons/fi';
import { BsFilterRight } from 'react-icons/bs';
import { CiCalendar } from 'react-icons/ci';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './Calendar.css';
import { dashboardService } from '../../services/dashboardService';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/userService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { formatDateTime, formatDateOnly, formatTimeOnly } from '../../utils/dateUtils';

// Calendar için moment localizer
const localizer = momentLocalizer(moment);

const Dashboard = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  // Calendar state'leri
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  const [showGoals, setShowGoals] = useState(false);
  const [isCalendarFullscreen, setIsCalendarFullscreen] = useState(false);
  const [showMeetingFilter, setShowMeetingFilter] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterType, setFilterType] = useState('both'); // 'created', 'participant', 'both'

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Filtre dropdown'ını kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMeetingFilter && !event.target.closest('.relative')) {
        setShowMeetingFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMeetingFilter]);

  // Tıklanabilir rakamlar için navigasyon fonksiyonları
  const handleCustomerClick = (status) => {
    if (status === 'active') {
      navigate(`/customers?status=active&manager=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    } else if (status === 'inactive') {
      navigate(`/customers?status=inactive&manager=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    } else {
      navigate(`/customers?manager=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    }
  };

  const handleProjectClick = (status) => {
    if (status === 'active') {
      navigate(`/projects?status=Aktif&assigned_to=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    } else if (status === 'completed') {
      navigate(`/projects?status=Başarılı Tamamlandı&assigned_to=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    } else if (status === 'pending') {
      navigate(`/projects?status=Beklemede&assigned_to=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    } else {
      navigate(`/projects?assigned_to=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    }
  };

  const handleOfferClick = (status) => {
    if (status === 'approved') {
      navigate(`/offers?status=Teklif Onaylandı&created_by=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    } else if (status === 'pending') {
      navigate(`/offers?status=Müşteri Onay Sürecinde,Gönderildi&created_by=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    } else if (status === 'rejected') {
      navigate(`/offers?status=Teklif Kabul Edilmedi&created_by=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    } else {
      navigate(`/offers?created_by=${encodeURIComponent(currentUser?.user_fullname || '')}`);
    }
  };

  // Fullscreen modda body scroll'unu engelle
  useEffect(() => {
    if (isCalendarFullscreen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
      document.body.style.height = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
      document.body.style.height = 'unset';
    };
  }, [isCalendarFullscreen]);
  
  // Meeting popup state'leri
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showGoalsModal, setShowGoalsModal] = useState(false);

  // Dashboard istatistiklerini al
  const { data: statsData, isLoading: statsLoading } = useQuery(
    'dashboard-stats',
    dashboardService.getStats,
    { refetchInterval: 30000 } // 30 saniyede bir güncelle
  );

  // Kullanıcıya özel detaylı istatistikleri al
  const { data: userStatsData, isLoading: userStatsLoading } = useQuery(
    'dashboard-user-stats',
    dashboardService.getUserStats,
    { refetchInterval: 30000 } // 30 saniyede bir güncelle
  );

  // Bekleyen teklifleri al
  const { data: pendingOffersData, isLoading: pendingOffersLoading, error: pendingOffersError } = useQuery(
    'dashboard-pending-offers',
    dashboardService.getPendingOffers,
    { 
      refetchInterval: 60000, // 1 dakikada bir güncelle
      cacheTime: 0, // Cache'i devre dışı bırak
      staleTime: 0, // Her zaman fresh data çek
      refetchOnMount: true, // Mount'ta her zaman yenile
    }
  );

  // Takvim için toplantıları al (filtreye göre)
  const { data: meetingsData, isLoading: meetingsLoading } = useQuery(
    ['meetings', selectedUsers, filterType],
    () => {
      if (selectedUsers.length === 0) {
        return dashboardService.getAllMeetings();
      } else if (selectedUsers.length === 1) {
        return dashboardService.getUserMeetings(selectedUsers[0], filterType);
      } else {
        return dashboardService.getMultipleUsersMeetings(selectedUsers, filterType);
      }
    },
    { refetchInterval: 60000 } // 1 dakikada bir güncelle
  );

  // Kullanıcıları al (filtreleme için)
  const { data: usersData } = useQuery(
    'users',
    dashboardService.getUsers
  );




  // Kullanıcının yıllık hedeflerini al
  const { data: myGoalsData } = useQuery(
    ['my-goals', currentUser?.id],
    () => userService.getUserGoals(currentUser.id),
    { enabled: !!currentUser?.id }
  );

  // Sadece bu yıla ait hedefleri filtrele
  const currentYearGoals = useMemo(() => {
    if (!myGoalsData?.goals) return [];
    const currentYear = new Date().getFullYear();
    const filtered = myGoalsData.goals.filter(goal => 
      goal.year === currentYear || 
      goal.year === currentYear.toString() || 
      parseInt(goal.year) === currentYear
    );
    return filtered;
  }, [myGoalsData]);

  const { data: myGoalsProgress } = useQuery(
    ['my-goals-progress', currentUser?.id],
    () => userService.getUserGoalsProgress(currentUser.id),
    { enabled: !!currentUser?.id }
  );


  const stats = statsData?.stats || {};
  const userStats = userStatsData?.userStats || {};
  const allMeetings = meetingsData?.meetings || [];
  const pendingOffers = pendingOffersData?.offers || [];
  
  // Sadece yaklaşan ve "Planlandı" durumundaki toplantıları filtrele
  // Ayrıca filtreleme yapıldığında sadece seçilen kullanıcıların toplantılarını göster
  const meetings = useMemo(() => {
    const now = new Date();
    
    // Önce geçmiş toplantıları ve sadece "Planlandı" durumundaki toplantıları filtrele
    const upcomingMeetings = allMeetings.filter(meeting => 
      new Date(meeting.meeting_start_date) >= now && 
      meeting.meeting_status === 'Planlandı'
    );
    
    // Eğer filtreleme yapılmışsa, sadece seçilen kullanıcıların toplantılarını göster
    if (selectedUsers.length > 0) {
      // Filtreleme tipine göre farklı işlem yap
      if (filterType === 'created') {
        // Sadece seçilen kullanıcıların yarattığı toplantılar
        return upcomingMeetings.filter(meeting => 
          selectedUsers.includes(meeting.created_by)
        );
      } else if (filterType === 'participant') {
        // Sadece seçilen kullanıcıların katılımcı olduğu toplantılar
        return upcomingMeetings.filter(meeting => {
          if (!meeting.meeting_participation) return false;
          try {
            const participants = JSON.parse(meeting.meeting_participation);
            return participants.some(participant => 
              selectedUsers.includes(parseInt(participant.id))
            );
          } catch (error) {
            console.error('Katılımcı verileri parse edilemedi:', error);
            return false;
          }
        });
      } else { // 'both'
        // Hem yarattığı hem katılımcı olduğu toplantılar
        return upcomingMeetings.filter(meeting => {
          // Kullanıcının yarattığı toplantılar
          if (selectedUsers.includes(meeting.created_by)) return true;
          
          // Kullanıcının katılımcı olduğu toplantılar
          if (meeting.meeting_participation) {
            try {
              const participants = JSON.parse(meeting.meeting_participation);
              return participants.some(participant => 
                selectedUsers.includes(parseInt(participant.id))
              );
            } catch (error) {
              console.error('Katılımcı verileri parse edilemedi:', error);
              return false;
            }
          }
          
          return false;
        });
      }
    }
    
    // Filtreleme yoksa tüm yaklaşan toplantıları göster
    return upcomingMeetings;
  }, [allMeetings, selectedUsers, filterType]);


  // Calendar events'lerini hazırla
  const calendarEvents = useMemo(() => {
    return allMeetings.map(meeting => ({
      id: meeting.id,
      title: meeting.meeting_subject,
      start: new Date(meeting.meeting_start_date),
      end: new Date(meeting.meeting_end_date),
      resource: {
        customer: meeting.customer_name,
        project: meeting.project_name,
        location: meeting.meeting_location,
        type: meeting.meeting_type,
        description: meeting.meeting_description,
        participation: meeting.meeting_participation,
        meeting_source: meeting.meeting_source,
        created_by: meeting.created_by,
        created_by_name: meeting.created_by_name
      }
    }));
  }, [meetings]);

  if (statsLoading || userStatsLoading) {
    return <LoadingSpinner text="Dashboard yükleniyor..." />;
  }

  // Calendar navigation fonksiyonları
  const navigateCalendar = (action) => {
    if (action === 'PREV') {
      setDate(moment(date).subtract(1, view === 'week' ? 'week' : 'month').toDate());
    } else if (action === 'NEXT') {
      setDate(moment(date).add(1, view === 'week' ? 'week' : 'month').toDate());
    } else if (action === 'TODAY') {
      setDate(new Date());
    }
  };

  // Takvim için kompakt özelleştirilmiş toolbar (mobil + desktop)
  const CustomToolbar = (toolbar) => {
    const sizeCls = isMobile ? 'text-xs' : 'text-sm';
    const padX = isMobile ? 'px-2' : 'px-3';
    const iconSize = isMobile ? 14 : 16;
    const btnBase = `${padX} py-1 ${sizeCls} rounded-md transition-colors text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700`;
    const chipBase = `${padX} py-1 ${sizeCls} rounded-md transition-colors`;
    const activeChip = 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm';
    const passiveChip = 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white';

    return (
      <div className={`flex items-center justify-between mb-3 flex-wrap gap-2`}>
        {/* Sol taraf: Navigasyon */}
        <div className="flex items-center space-x-1">
          <button onClick={() => toolbar.onNavigate('PREV')} className={`${btnBase} p-2`}>
            <FiChevronLeft size={iconSize} />
          </button>
          <button onClick={() => toolbar.onNavigate('TODAY')} className={`${btnBase}`}>
            Bugün
          </button>
          <button onClick={() => toolbar.onNavigate('NEXT')} className={`${btnBase} p-2`}>
            <FiChevronRight size={iconSize} />
          </button>
        </div>

        {/* Orta: Tarih etiketi */}
        <div className={`font-semibold ${isMobile ? 'text-sm' : 'text-base'} text-gray-900 dark:text-white`}>
          {toolbar.label}
        </div>

        {/* Sağ taraf: Görünüm seçimleri */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 space-x-1">
          <button
            onClick={() => toolbar.onView('month')}
            className={`${chipBase} ${toolbar.view === 'month' ? activeChip : passiveChip}`}
          >
            Aylık
          </button>
          <button
            onClick={() => toolbar.onView('week')}
            className={`${chipBase} ${toolbar.view === 'week' ? activeChip : passiveChip}`}
          >
            Haftalık
          </button>
          <button
            onClick={() => toolbar.onView('day')}
            className={`${chipBase} ${toolbar.view === 'day' ? activeChip : passiveChip}`}
          >
            Günlük
          </button>
          <button
            onClick={() => toolbar.onView('agenda')}
            className={`${chipBase} ${toolbar.view === 'agenda' ? activeChip : passiveChip}`}
          >
            Ajanda
          </button>
        </div>
      </div>
    );
  };

  // Meeting click handler
  const handleMeetingClick = (event) => {
    setSelectedMeeting(event);
    setShowMeetingModal(true);
  };

  // Modal kapatma
  const closeMeetingModal = () => {
    setShowMeetingModal(false);
    setSelectedMeeting(null);
  };

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <FiHome size={24} />
          </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Dashboard
        </h1>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Son güncelleme: {new Date().toLocaleString('tr-TR')}
        </div>
      </div>

      {/* İstatistik kartları ve Hedeflerim - tek satır */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Hedeflerim - kompakt liste */}
        <div 
          onClick={() => setShowGoalsModal(true)}
          className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-4 border border-purple-100 dark:border-gray-600 hover:shadow-lg transition-all duration-300 cursor-pointer"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full">
                <FiTarget className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                Hedeflerim
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowGoals((v) => !v);
              }}
              className="flex items-center justify-center w-8 h-8 text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 hover:bg-amber-100 dark:hover:bg-gray-600 rounded-full transition-colors"
              aria-label={showGoals ? 'Hedefleri gizle' : 'Hedefleri göster'}
              title={showGoals ? 'Hedefleri gizle' : 'Hedefleri göster'}
            >
              {showGoals ? <FiEyeOff size={16} /> : <FiEye size={16} />}
            </button>
          </div>
          
            {currentYearGoals.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-blue-100 dark:border-gray-600">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="flex items-center justify-center w-5 h-5 bg-blue-100 dark:bg-blue-900 rounded-full">
                    <FiUsers className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Müşteri Hedefi ({currentYearGoals[0]?.year || new Date().getFullYear()})
                    </span>
                </div>
                <div className="text-center">
                  <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {showGoals ? (currentYearGoals[0]?.customer_target || 0) : '***'}
                    </span>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-emerald-100 dark:border-gray-600">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="flex items-center justify-center w-5 h-5 bg-emerald-100 dark:bg-emerald-900 rounded-full">
                    <FiDollarSign className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Kârlılık Hedefi ({currentYearGoals[0]?.year || new Date().getFullYear()})
                  </span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    {showGoals
                      ? new Intl.NumberFormat('tr-TR', { 
                          style: 'currency', 
                          currency: currentYearGoals[0]?.profitability_currency || 'TRY',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        }).format(Number(currentYearGoals[0]?.profitability_target || 0))
                      : '***'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-amber-200 dark:border-gray-600">
              <div className="flex flex-col items-center space-y-3">
                <div className="flex items-center justify-center w-12 h-12 bg-amber-100 dark:bg-gray-600 rounded-full">
                  <FiTarget className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="text-gray-500 dark:text-gray-400">
                  <p className="font-medium">
                {myGoalsData?.goals?.length > 0 
                  ? 'Bu yıl için hedef bulunamadı' 
                  : 'Hedef bulunamadı'
                }
                  </p>
                  <p className="text-xs mt-1">Hedeflerinizi ekleyerek başlayın</p>
                </div>
              </div>
              </div>
            )}
        </div>

        {/* Müşterilerim */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-4 border border-purple-100 dark:border-gray-600 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full">
                <FiUsers className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Müşterilerim
              </span>
            </div>
            <span 
              className="text-2xl font-bold text-blue-600 dark:text-blue-400 cursor-pointer hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
              onClick={() => handleCustomerClick('total')}
              title="Tüm müşterileri görüntüle"
            >
              {userStats.customers?.total || 0}
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-blue-100 dark:border-gray-600">
              <div className="flex items-center space-x-2 mb-1">
                <div className="flex items-center justify-center w-5 h-5 bg-green-100 dark:bg-green-900 rounded-full">
                  <FiCheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Aktif</span>
              </div>
              <div className="text-center">
                <span 
                  className="text-lg font-bold text-green-600 dark:text-green-400 cursor-pointer hover:text-green-800 dark:hover:text-green-200 transition-colors"
                  onClick={() => handleCustomerClick('active')}
                  title="Aktif müşterileri görüntüle"
                >
                  {userStats.customers?.active || 0}
                </span>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-gray-100 dark:border-gray-600">
              <div className="flex items-center space-x-2 mb-1">
                <div className="flex items-center justify-center w-5 h-5 bg-red-100 dark:bg-red-900 rounded-full">
                  <FiXCircle className="w-3 h-3 text-red-600 dark:text-red-400" />
                </div>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Pasif</span>
              </div>
              <div className="text-center">
                <span 
                  className="text-lg font-bold text-red-600 dark:text-red-400 cursor-pointer hover:text-red-800 dark:hover:text-red-200 transition-colors"
                  onClick={() => handleCustomerClick('inactive')}
                  title="Pasif müşterileri görüntüle"
                >
                  {userStats.customers?.inactive || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Projelerim */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-4 border border-purple-100 dark:border-gray-600 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full">
                <FiFolder className="w-4 h-4 text-white" />
            </div>
              <span className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                Projelerim
              </span>
            </div>
            <span 
              className="text-2xl font-bold text-green-600 dark:text-green-400 cursor-pointer hover:text-green-800 dark:hover:text-green-200 transition-colors"
              onClick={() => handleProjectClick('total')}
              title="Tüm projeleri görüntüle"
            >
              {userStats.projects?.total || 0}
            </span>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-green-100 dark:border-gray-600">
              <div className="flex items-center space-x-1 mb-1">
                <FiPlay className="w-3 h-3 text-green-600 dark:text-green-400" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Aktif</span>
              </div>
              <div className="text-center">
                <span 
                  className="text-sm font-bold text-green-600 dark:text-green-400 cursor-pointer hover:text-green-800 dark:hover:text-green-200 transition-colors"
                  onClick={() => handleProjectClick('active')}
                  title="Aktif projeleri görüntüle"
                >
                  {userStats.projects?.active || 0}
                </span>
              </div>
        </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-blue-100 dark:border-gray-600">
              <div className="flex items-center space-x-1 mb-1">
                <FiCheckCircle className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Tamam</span>
              </div>
              <div className="text-center">
                <span 
                  className="text-sm font-bold text-blue-600 dark:text-blue-400 cursor-pointer hover:text-blue-800 dark:hover:text-blue-200 transition-colors"
                  onClick={() => handleProjectClick('completed')}
                  title="Tamamlanan projeleri görüntüle"
                >
                  {userStats.projects?.completed || 0}
                </span>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-orange-100 dark:border-gray-600">
              <div className="flex items-center space-x-1 mb-1">
                <FiPause className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Bekle</span>
              </div>
              <div className="text-center">
                <span 
                  className="text-sm font-bold text-orange-600 dark:text-orange-400 cursor-pointer hover:text-orange-800 dark:hover:text-orange-200 transition-colors"
                  onClick={() => handleProjectClick('pending')}
                  title="Bekleyen projeleri görüntüle"
                >
                  {userStats.projects?.pending || 0}
                </span>
              </div>
            </div>
          </div>
        </div>


        {/* Tekliflerim */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-4 border border-purple-100 dark:border-gray-600 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                <FiFileText className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Tekliflerim
              </span>
            </div>
            <span 
              className="text-2xl font-bold text-purple-600 dark:text-purple-400 cursor-pointer hover:text-purple-800 dark:hover:text-purple-200 transition-colors"
              onClick={() => handleOfferClick('total')}
              title="Tüm teklifleri görüntüle"
            >
              {userStats.offers?.total || 0}
            </span>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-green-100 dark:border-gray-600">
              <div className="flex items-center space-x-1 mb-1">
                <FiThumbsUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Onay</span>
              </div>
              <div className="text-center">
                <span 
                  className="text-sm font-bold text-green-600 dark:text-green-400 cursor-pointer hover:text-green-800 dark:hover:text-green-200 transition-colors"
                  onClick={() => handleOfferClick('approved')}
                  title="Onaylanan teklifleri görüntüle"
                >
                  {userStats.offers?.approved || 0}
                </span>
              </div>
        </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-orange-100 dark:border-gray-600">
              <div className="flex items-center space-x-1 mb-1">
                <FiPause className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Bekle</span>
              </div>
              <div className="text-center">
                <span 
                  className="text-sm font-bold text-orange-600 dark:text-orange-400 cursor-pointer hover:text-orange-800 dark:hover:text-orange-200 transition-colors"
                  onClick={() => handleOfferClick('pending')}
                  title="Bekleyen teklifleri görüntüle"
                >
                  {userStats.offers?.pending || 0}
                </span>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm border border-red-100 dark:border-gray-600">
              <div className="flex items-center space-x-1 mb-1">
                <FiThumbsDown className="w-3 h-3 text-red-600 dark:text-red-400" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Red</span>
              </div>
              <div className="text-center">
                <span 
                  className="text-sm font-bold text-red-600 dark:text-red-400 cursor-pointer hover:text-red-800 dark:hover:text-red-200 transition-colors"
                  onClick={() => handleOfferClick('rejected')}
                  title="Reddedilen teklifleri görüntüle"
                >
                  {userStats.offers?.rejected || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Sol taraf - Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sol - Calendar */}
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-slate-200 dark:border-gray-600 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-slate-500 to-gray-500 rounded-xl shadow-md">
              <FiCalendar className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold bg-gradient-to-r from-slate-600 to-gray-600 bg-clip-text text-transparent">
              Takvim
            </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {calendarEvents.length > 0 ? `${calendarEvents.length} etkinlik` : 'Etkinlik yok'}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <button
                  onClick={() => setShowMeetingFilter(!showMeetingFilter)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                  title="Toplantı Filtresi"
                >
                  <BsFilterRight size={20} />
                </button>
                
                {showMeetingFilter && (
                  <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[60]">
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-w-md w-full">
                      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Toplantı Filtresi</h3>
                        <button
                          onClick={() => setShowMeetingFilter(false)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <FiX size={20} />
                        </button>
                      </div>
                      
                      <div className="p-4">
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kullanıcılar</label>
                          <div className="max-h-48 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-700 rounded-md p-2">
                            {usersData?.users?.map(user => (
                              <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.includes(user.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedUsers([...selectedUsers, user.id]);
                                    } else {
                                      setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                    }
                                  }}
                                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                />
                                <span className="text-sm text-gray-900 dark:text-white">
                                  {user.user_fullname}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        {selectedUsers.length > 0 && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Toplantı Tipi</label>
                            <div className="space-y-2 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                              <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
                                <input
                                  type="radio"
                                  name="filterType"
                                  checked={filterType === 'created'}
                                  onChange={() => setFilterType('created')}
                                  className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                />
                                <span className="text-sm text-gray-900 dark:text-white">Yarattığı toplantılar</span>
                              </label>
                              
                              <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
                                <input
                                  type="radio"
                                  name="filterType"
                                  checked={filterType === 'participant'}
                                  onChange={() => setFilterType('participant')}
                                  className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                />
                                <span className="text-sm text-gray-900 dark:text-white">Katılımcı olduğu toplantılar</span>
                              </label>
                              
                              <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
                                <input
                                  type="radio"
                                  name="filterType"
                                  checked={filterType === 'both'}
                                  onChange={() => setFilterType('both')}
                                  className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                                />
                                <span className="text-sm text-gray-900 dark:text-white">Her ikisi de</span>
                              </label>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={() => {
                              setSelectedUsers([]);
                              setFilterType('both');
                            }}
                            className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Filtreyi Temizle
                          </button>
                          
                          <button
                            onClick={() => setShowMeetingFilter(false)}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                          >
                            Uygula
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setIsCalendarFullscreen(!isCalendarFullscreen)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                title={isCalendarFullscreen ? "Küçült" : "Tam Ekran"}
              >
                {isCalendarFullscreen ? <FiX size={20} /> : <FiMaximize size={20} />}
              </button>
            </div>
          </div>
          
          {/* Filtre Uyarı Mesajı */}
          {selectedUsers.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <BsFilterRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-blue-800 dark:text-blue-200">
                    Filtre: <strong>{selectedUsers.length} kullanıcı</strong>
                    {selectedUsers.length <= 3 && (
                      <span className="ml-1">
                        ({usersData?.users?.filter(u => selectedUsers.includes(u.id)).map(u => u.user_fullname).join(', ')})
                      </span>
                    )}
                    {filterType === 'created' && <span className="ml-1 font-medium"> - Yarattığı toplantılar</span>}
                    {filterType === 'participant' && <span className="ml-1 font-medium"> - Katılımcı olduğu toplantılar</span>}
                    {filterType === 'both' && <span className="ml-1 font-medium"> - Tüm toplantılar</span>}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setSelectedUsers([]);
                    setFilterType('both');
                  }}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                  title="Filtreyi temizle"
                >
                  <FiX size={16} />
                </button>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-600 shadow-sm overflow-hidden">
            <div className={`h-[420px] lg:h-[600px] p-2`}>
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              view={view}
              date={date}
              onNavigate={setDate}
              onView={setView}
              onSelectEvent={handleMeetingClick}
              views={[ 'month', 'week', 'day', 'agenda' ]}
              style={{ 
                height: '100%',
                fontSize: isMobile ? '10px' : '12px'
              }}
              eventPropGetter={(event) => {
                const now = new Date();
                const eventDate = new Date(event.start);
                const isPast = eventDate < now;
                
                let backgroundColor;
                if (isPast) {
                  backgroundColor = '#6B7280'; // Gray for past meetings
                } else {
                  backgroundColor = event.resource?.meeting_source === 'customer' 
                    ? '#10B981' // Emerald for customer meetings
                    : '#3B82F6'; // Blue for project meetings
                }
                
                return {
                  style: {
                    backgroundColor,
                    border: 'none',
                    borderRadius: '0px',
                    color: 'white',
                    fontSize: isMobile ? '10px' : '11px',
                    padding: '2px 6px',
                    lineHeight: '1.3',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    fontWeight: '500',
                    opacity: isPast ? 0.7 : 1
                  }
                };
              }}
              components={{
                toolbar: CustomToolbar,
                event: ({ event }) => {
                  const startTime = new Date(event.start).toLocaleTimeString('tr-TR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  });
                  
                  return (
                    <div className={`${isMobile ? 'text-[10px]' : 'text-xs'} leading-tight`}>
                        <div className={`font-semibold truncate ${isMobile ? 'text-[10px]' : 'text-xs'} flex items-center space-x-1`}>
                          <span>{event.title}</span>
                          {event.resource?.meeting_source === 'customer' && (
                            <span className="text-emerald-200">👤</span>
                          )}
                          {event.resource?.meeting_source === 'project' && (
                            <span className="text-blue-200">📁</span>
                          )}
                        </div>
                    </div>
                  );
                }
              }}
            />
            </div>
            
            {/* Renk Açıklaması Legend */}
            <div className="mt-4 mb-6 flex items-center justify-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-emerald-500 rounded-sm"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                  Müşteri Toplantısı
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded-sm"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                  Proje Toplantısı
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-gray-500 rounded-sm"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                  Geçmiş Toplantı
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Sağ taraf - Yaklaşan Toplantılar ve Bekleyen Teklifler */}
        <div className="grid grid-cols-1 gap-6">
          {/* Sağ üst - Yaklaşan Toplantılar */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-indigo-100 dark:border-gray-600 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl shadow-md">
                <FiCalendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Yaklaşan Toplantılar
              </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {meetings.length > 0 ? `${meetings.length} toplantı planlandı` : 'Toplantı yok'}
                </p>
              </div>
            </div>
            
            <div className="flex-1">
              {meetingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Toplantılar yükleniyor...</span>
                  </div>
                </div>
              ) : meetings.length > 0 ? (
                <div className="space-y-3">
                  {meetings.slice(0, 5).map((meeting, index) => {
                    const meetingDate = new Date(meeting.meeting_start_date);
                    const isToday = meetingDate.toDateString() === new Date().toDateString();
                    const isTomorrow = meetingDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
                    
                    return (
                      <div 
                        key={meeting.id} 
                        onClick={() => {
                          setSelectedMeeting({
                            id: meeting.id,
                            title: meeting.meeting_subject,
                            start: meeting.meeting_start_date,
                            end: meeting.meeting_end_date,
                            resource: {
                              customer: meeting.customer_name,
                              project: meeting.project_name,
                              type: meeting.meeting_type,
                              location: meeting.meeting_location,
                              description: meeting.meeting_description,
                              participation: meeting.meeting_participation,
                              reason: meeting.meeting_reason,
                              status: meeting.meeting_status,
                              meeting_source: meeting.meeting_source,
                              created_by: meeting.created_by,
                              created_by_name: meeting.created_by_name
                            }
                          });
                          setShowMeetingModal(true);
                        }}
                        className="group relative bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-600 hover:border-indigo-200 dark:hover:border-indigo-500 transition-all duration-300 hover:shadow-md transform hover:scale-[1.02] cursor-pointer"
                      >
                        {/* Tarih etiketi */}
                        <div className="absolute -top-2 -right-2">
                          <div className={`px-2 py-1 rounded-full text-xs font-bold shadow-sm ${
                            isToday 
                              ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white' 
                              : isTomorrow 
                                ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white'
                                : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white'
                          }`}>
                            {isToday 
                              ? 'BUGÜN' 
                              : isTomorrow 
                                ? 'YARIN' 
                                : formatDateOnly(meeting.meeting_start_date)
                            }
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          {/* Sol taraf - Zaman */}
                          <div className="flex-shrink-0 text-center">
                            <div className="bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 rounded-lg p-5 min-w-[110px]">
                              <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                                {formatDateOnly(meeting.meeting_start_date)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {formatTimeOnly(meeting.meeting_start_date)}
                              </div>
                            </div>
                            
                            {/* Toplantı Lokasyonu - Sol kutunun altına */}
                            {meeting.meeting_location && (
                              <div className="flex items-center justify-center space-x-1 mt-2">
                                <FiMapPin className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                                  {meeting.meeting_location}
                                </span>
                              </div>
                            )}
                      </div>

                          {/* Orta - İçerik */}
                      <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                          {meeting.customer_name}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                meeting.meeting_type === 'Online' 
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              }`}>
                              {meeting.meeting_type}
                            </span>
                          </div>
                            
                            <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                              {meeting.project_name || 'Müşteri Toplantısı'}
                            </p>
                            
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              {meeting.meeting_subject}
                            </p>
                            
                            {/* Toplantıyı Yaratan */}
                            <div className="flex items-center space-x-1 mb-2">
                              <FiUser className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Yaratan: {meeting.created_by_name || 'Belirtilmemiş'}
                              </span>
                            </div>
                            
                            {/* Katılımcılar - En alta taşındı */}
                            <div className="flex items-center space-x-1 mt-2">
                              <FiUsers className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Katılımcılar: 
                              </span>
                              {meeting.meeting_participation ? (() => {
                                try {
                                  const participants = JSON.parse(meeting.meeting_participation);
                                  return participants.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {participants.slice(0, 3).map((participant, index) => (
                                        <span 
                                          key={index}
                                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium"
                                        >
                                          {participant.user_fullname}
                                        </span>
                                      ))}
                                      {participants.length > 3 && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          +{participants.length - 3} daha
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-red-500 dark:text-red-400">
                                      Katılımcı eklenmemiştir
                                    </span>
                                  );
                                } catch (error) {
                                  return (
                                    <span className="text-xs text-red-500 dark:text-red-400">
                                      Katılımcı eklenmemiştir
                                    </span>
                                  );
                                }
                              })() : (
                                <span className="text-xs text-red-500 dark:text-red-400">
                                  Katılımcı eklenmemiştir
                                </span>
                              )}
                            </div>
                        </div>

                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-full flex items-center justify-center">
                      <FiCalendar className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">
                  Yaklaşan toplantı bulunmuyor
                </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Yeni toplantılar eklendiğinde burada görünecek
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sağ alt - Bekleyen Teklifler */}
          <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-orange-100 dark:border-gray-600 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl shadow-md">
                <FiFileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  Bekleyen Teklifler
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {pendingOffers.length > 0 ? `${pendingOffers.length} teklif bekliyor` : 'Bekleyen teklif yok'}
                </p>
              </div>
            </div>
            
            <div className="flex-1">
              {pendingOffersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Teklifler yükleniyor...</span>
                  </div>
                </div>
              ) : pendingOffers.length > 0 ? (
                <div className="space-y-3">
                  {pendingOffers.slice(0, 5).map((offer, index) => {
                    const createdDate = new Date(offer.created_at);
                    const validUntil = offer.offer_valid_until ? new Date(offer.offer_valid_until) : null;
                    const isExpiringSoon = validUntil && validUntil <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 gün içinde
                    
                    return (
                      <div 
                        key={offer.id} 
                        onClick={() => navigate(`/offers/${offer.id}`)}
                        className="group relative bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-600 hover:border-orange-200 dark:hover:border-orange-500 transition-all duration-300 hover:shadow-md transform hover:scale-[1.02] cursor-pointer"
                      >
                        {/* Durum etiketi */}
                        <div className="absolute -top-2 -right-2">
                          <div className="px-2 py-1 rounded-full text-xs font-bold shadow-sm bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                            {offer.offer_status === 'Gönderildi' ? 'GÖNDERİLDİ' : 'MÜŞTERİ ONAY SÜRECİNDE'}
                          </div>
                        </div>

                        <div className="flex items-start space-x-3">
                          {/* Sol taraf - Teklif Iconu */}
                          <div className="flex-shrink-0 text-center">
                            <div className="bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900 dark:to-red-900 rounded-lg p-3 min-w-[60px]">
                              <FiFileText className="w-6 h-6 text-orange-600 dark:text-orange-400 mx-auto" />
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {createdDate.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
                              </div>
                            </div>
                          </div>

                          {/* Sağ taraf - İçerik */}
                          <div className="flex-1 min-w-0">
                            {/* 1. Proje Teklifi ya da Müşteri Teklifi */}
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                {offer.project_name ? 'Proje Teklifi' : 'Müşteri Teklifi'}
                              </h4>
                              {isExpiringSoon && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
                                  Süresi Doluyor
                                </span>
                              )}
                            </div>
                            
                            {/* 2. Teklif açıklaması */}
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              {offer.offer_title}
                            </p>
                            
                            {/* 3. Müşteri Adı / Proje adı */}
                            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                              {offer.project_name ? `${offer.customer_name} / ${offer.project_name}` : (offer.customer_name || 'Müşteri Adı Yok')}
                            </p>

                            {/* 4. Teklif tutarı */}
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-bold text-orange-600 dark:text-orange-400">
                                {new Intl.NumberFormat('tr-TR', {
                                  style: 'currency',
                                  currency: (offer.offer_currency === 'TL' || !offer.offer_currency) ? 'TRY' : offer.offer_currency,
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0
                                }).format(Number(offer.offer_amount || 0))}
                              </div>
                              {validUntil && (
                                <div className="flex items-center space-x-1">
                                  <FiClock className="w-3 h-3 text-gray-400" />
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Geçerlilik: {validUntil.toLocaleDateString('tr-TR')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-full flex items-center justify-center">
                      <FiFileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">
                        Bekleyen teklif bulunmuyor
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Yeni teklifler eklendiğinde burada görünecek
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>


      </div>

      {/* Meeting Detail Modal */}
      {showMeetingModal && selectedMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="modal-content">
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  Toplantı Detayları
                </h3>
                <button
                  onClick={closeMeetingModal}
                  className="text-white hover:text-gray-300"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Toplantı Başlığı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Toplantı Konusu
                </label>
                <p className="text-gray-900 dark:text-white font-medium">
                  {selectedMeeting.title}
                </p>
              </div>
              
              {/* Toplantıyı Yaratan Kişi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Toplantıyı Yaratan
                </label>
                <p className="text-gray-900 dark:text-white">
                  {selectedMeeting.resource?.created_by_name || 'Belirtilmemiş'}
                </p>
              </div>

              {/* Müşteri */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Müşteri
                </label>
                <p className="text-gray-900 dark:text-white">
                  {selectedMeeting.resource?.customer}
                </p>
              </div>

              {/* Proje */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Proje
                </label>
                <p className="text-gray-900 dark:text-white">
                  {selectedMeeting.resource?.project || 'Belirtilmemiş'}
                </p>
                {selectedMeeting.resource?.meeting_source === 'customer' && (
                  <p className="text-xs text-red-500 mt-1 italic">
                    Bu bir proje toplantısı değil, müştri toplantısıdır
                  </p>
                )}
          </div>
          
              {/* Tarih ve Saat */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Başlangıç
                  </label>
                  <div className="flex items-center space-x-2">
                    <CiCalendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900 dark:text-white">
                      {formatDateTime(selectedMeeting.start instanceof Date ? selectedMeeting.start.toISOString() : selectedMeeting.start)}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bitiş
                  </label>
                  <div className="flex items-center space-x-2">
                    <CiCalendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900 dark:text-white">
                      {formatDateTime(selectedMeeting.end instanceof Date ? selectedMeeting.end.toISOString() : selectedMeeting.end)}
                    </span>
                  </div>
                </div>
        </div>

              {/* Toplantı Tipi ve Konum */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Toplantı Tipi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Toplantı Tipi
                  </label>
                  <div className="flex items-center space-x-2">
                    <FiType className="h-4 w-4 text-blue-500" />
                    <span className="text-gray-900 dark:text-white font-medium">
                      {selectedMeeting.resource?.type || 'Belirtilmemiş'}
                    </span>
                  </div>
          </div>
          
                {/* Konum */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Toplantı Yeri
                  </label>
                  <div className="flex items-center space-x-2">
                    <FiMapPin className="h-4 w-4 text-green-500" />
                    <span className="text-gray-900 dark:text-white font-medium">
                      {selectedMeeting.resource?.location || 'Belirtilmemiş'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Açıklama */}
              {selectedMeeting.resource?.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Açıklama
                  </label>
                  <p className="text-gray-900 dark:text-white text-sm">
                    {selectedMeeting.resource.description}
                  </p>
                </div>
              )}

              {/* Katılımcılar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Katılımcılar
                </label>
                {selectedMeeting.resource?.participation ? (
                  <div className="space-y-1">
                    {JSON.parse(selectedMeeting.resource.participation).length > 0 ? (
                      JSON.parse(selectedMeeting.resource.participation).map((participant, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <FiUser className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-900 dark:text-white text-sm">
                            {participant.user_fullname}
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-red-500 text-sm">
                        Katılımcı eklenmemiştir
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-red-500 text-sm">
                    Katılımcı eklenmemiştir
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeMeetingModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Goals Detail Modal */}
      {showGoalsModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-3xl w-full">
            <div className="p-4 bg-brand-primary">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Hedefler ve Gerçekleşme</h3>
                <button onClick={() => setShowGoalsModal(false)} className="text-white hover:text-gray-300">
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {(myGoalsProgress?.goals || []).length > 0 ? (
                  myGoalsProgress.goals.map((g) => (
                    <div key={g.id} className="rounded-lg bg-gray-50 dark:bg-gray-700 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{g.year}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200">{g.profitability_currency || 'TRY'}</span>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-200">
                        <div className="flex flex-wrap gap-4">
                          <span>
                            <strong>Hedef:</strong> {g.customer_target}
                          </span>
                          <span>
                            <strong>Gerçekleşen:</strong> {g.completed_count} (%{g.completion_rate})
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 mt-1">
                          <span>
                            <strong>Kârlılık Hedefi:</strong> {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: g.profitability_currency || 'TRY' }).format(Number(g.profitability_target || 0))}
                          </span>
                          <span>
                            <strong>Gerçekleşen:</strong> {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: g.profitability_currency || 'TRY' }).format(Number(g.approved_profitability || 0))} (%{g.profitability_rate})
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">Hedef bulunamadı</div>
                )}
              </div>
            </div>
            <div className="flex justify-end p-6">
              <button onClick={() => setShowGoalsModal(false)} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors">
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Calendar Modal */}
        {isCalendarFullscreen && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-white dark:bg-gray-900 z-[9999] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-slate-500 to-gray-500 rounded-lg shadow-md">
                <FiCalendar className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Takvim - Tam Ekran</h2>
            </div>
               <div className="flex items-center space-x-4">
                 <div className="flex items-center space-x-2">
                   <div className="relative">
                     <button
                       onClick={() => setShowMeetingFilter(!showMeetingFilter)}
                       className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                       title="Toplantı Filtresi"
                     >
                       <BsFilterRight size={20} />
                     </button>
                     
                     {showMeetingFilter && (
                       <div className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl z-50 min-w-64">
                         <div className="p-3">
                           <div className="flex items-center justify-between mb-3">
                             <span className="text-sm font-medium text-gray-900 dark:text-white">Kullanıcı Filtresi</span>
                             <div className="flex items-center space-x-2">
                               <button
                                 onClick={() => setSelectedUsers([])}
                                 className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                               >
                                 Temizle
                               </button>
                               <button
                                 onClick={() => setShowMeetingFilter(false)}
                                 className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                               >
                                 <FiX size={16} />
                               </button>
                             </div>
                           </div>
                           
                           <div className="max-h-48 overflow-y-auto space-y-2">
                             {usersData?.users?.map(user => (
                               <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded">
                                 <input
                                   type="checkbox"
                                   checked={selectedUsers.includes(user.id)}
                                   onChange={(e) => {
                                     if (e.target.checked) {
                                       setSelectedUsers([...selectedUsers, user.id]);
                                     } else {
                                       setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                     }
                                   }}
                                   className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                 />
                                 <span className="text-sm text-gray-900 dark:text-white">
                                   {user.user_fullname}
                                 </span>
                               </label>
                             ))}
                           </div>
                           
                           {selectedUsers.length > 0 && (
                             <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                               <div className="text-xs text-gray-500 dark:text-gray-400">
                                 {selectedUsers.length} kullanıcı seçildi
                               </div>
                             </div>
                           )}
                         </div>
                       </div>
                     )}
                   </div>
                 </div>
                 <button
                   onClick={() => setIsCalendarFullscreen(false)}
                   className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                   title="Küçült"
                 >
                   <FiX size={24} />
                 </button>
               </div>
          </div>
          
          {/* Fullscreen Filtre Uyarı Mesajı */}
          {selectedUsers.length > 0 && (
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <BsFilterRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm text-blue-800 dark:text-blue-200">
                    Filtre uygulandı: <strong>{selectedUsers.length} kullanıcı</strong> toplantıları gösteriliyor
                    {selectedUsers.length <= 3 && (
                      <span className="ml-1">
                        ({usersData?.users?.filter(u => selectedUsers.includes(u.id)).map(u => u.user_fullname).join(', ')})
                      </span>
                    )}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedUsers([])}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                  title="Filtreyi temizle"
                >
                  <FiX size={16} />
                </button>
              </div>
            </div>
          )}
          
          <div className="flex-1 p-2 overflow-hidden">
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              view={view}
              date={date}
              onNavigate={setDate}
              onView={setView}
              onSelectEvent={handleMeetingClick}
              views={['month', 'week', 'day', 'agenda']}
              style={{
                height: 'calc(100vh - 120px)',
                fontSize: '14px'
              }}
              eventPropGetter={(event) => {
                const now = new Date();
                const eventDate = new Date(event.start);
                const isPast = eventDate < now;
                
                let backgroundColor;
                if (isPast) {
                  backgroundColor = '#6B7280'; // Gray for past meetings
                } else {
                  backgroundColor = event.resource?.meeting_source === 'customer' 
                    ? '#10B981' // Emerald for customer meetings
                    : '#3B82F6'; // Blue for project meetings
                }
                
                return {
                  style: {
                    backgroundColor,
                    border: 'none',
                    borderRadius: '0px',
                    color: 'white',
                    fontSize: '12px',
                    padding: '4px 8px',
                    lineHeight: '1.3',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    fontWeight: '500',
                    opacity: isPast ? 0.7 : 1
                  }
                };
              }}
              components={{
                toolbar: CustomToolbar,
                event: ({ event }) => {
                  return (
                    <div className="text-xs leading-tight">
                        <div className="font-semibold truncate text-xs flex items-center space-x-1">
                          <span>{event.title}</span>
                          {event.resource?.meeting_source === 'customer' && (
                            <span className="text-emerald-200">👤</span>
                          )}
                          {event.resource?.meeting_source === 'project' && (
                            <span className="text-blue-200">📁</span>
                          )}
                        </div>
                    </div>
                  );
                }
              }}
            />
          </div>
          
          {/* Fullscreen Renk Açıklaması Legend */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-emerald-500 rounded-sm"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                  Müşteri Toplantısı
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded-sm"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                  Proje Toplantısı
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-gray-500 rounded-sm"></div>
                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                  Geçmiş Toplantı
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

